import React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import Media from 'react-media';

import { TooltipModel, theme } from '@notino/react-styleguide-v20';

import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { UnitPrice, IUnitPrice, CheckoutPrice } from '@sharedComponents/Prices';
import { AlignedInfoMark } from '@styles/cartComponents';
import { ReactFCV } from '@types';
import { stopPropagationMouseEvent } from '@utils/general';

import { useGeneralProductItemContext } from '../context';
import { ISizeProps } from '../model';
import {
  StyledPricePerUnitCol,
  StyledPricePerUnitContent,
  StyledOldPriceWrapper,
  StyledOldTotalPriceWrapper,
  StyledPricePerUnitWrapper,
  StyledPricePerUnitPlaceholderCol,
  StyledTooltipLink,
  SCheckoutPrice,
} from '../styled';

export interface IPricePerUnitProps extends ISizeProps {
  unitPrice?: IUnitPrice;
  inSaleInfoMarkText?: MessageDescriptor;
  inSaleInfoMarkTextLink?: MessageDescriptor;
  inSaleInfoMarkLink?: string;
}

export const PricePerUnit: ReactFCV<IPricePerUnitProps> = ({ unitPrice, ...rest }) => {
  const {
    applicationSettings: {
      features: { disableSalePrice },
      secondaryCurrency,
    },
  } = useApplicationRuntimeContext();
  const { price, disabled, priceHistory, itemQuantity } = useGeneralProductItemContext();
  const priceTotal = itemQuantity * price;
  const priceHistoryTotal = priceHistory ? itemQuantity * priceHistory : 0;
  const shouldRenderOldTotalPrice = priceHistoryTotal > 0 && priceTotal > 0;
  const shouldRenderTotalPrice = priceHistoryTotal <= 0 && priceTotal > 0;
  const PRODUCT_IN_SALE_TOOLTIP_WIDTH = 200;
  const showIsInSale = priceHistory && !disableSalePrice;
  const isDualPriceLayout = Boolean(secondaryCurrency);
  return (
    <>
      <Media query={`(max-width: ${theme.breakpoints.sm ?? ''}rem)`}>
        <StyledPricePerUnitPlaceholderCol xs={2} sm={1} disabled={disabled} {...rest} />
      </Media>
      <StyledPricePerUnitCol
        xs={2}
        sm={isDualPriceLayout ? 2 : 1}
        md={isDualPriceLayout ? 2 : 1}
        disabled={disabled}
        {...rest}>
        <StyledPricePerUnitContent className="price-per-unit-wrapper">
          <StyledPricePerUnitWrapper>
            {!disableSalePrice && priceHistory && (
              <StyledOldPriceWrapper dualPriceLayout={isDualPriceLayout}>
                <CheckoutPrice isColumnLayout={false} price={priceHistory} isHighLighted={false} isOldPrice={true} />
                <AlignedInfoMark
                  content={
                    <>
                      <FormattedMessage {...rest.inSaleInfoMarkText} />
                      <StyledTooltipLink href={rest.inSaleInfoMarkLink} onClick={stopPropagationMouseEvent}>
                        <FormattedMessage {...rest.inSaleInfoMarkTextLink} />
                      </StyledTooltipLink>
                    </>
                  }
                  interactive={true}
                  tooltipWidth={PRODUCT_IN_SALE_TOOLTIP_WIDTH}
                />
              </StyledOldPriceWrapper>
            )}
            {price > 0 && (
              <SCheckoutPrice
                isColumnLayout={!isDualPriceLayout}
                price={price}
                isHighLighted={!!showIsInSale}
                dualPriceLayout={isDualPriceLayout}
              />
            )}
          </StyledPricePerUnitWrapper>

          {shouldRenderOldTotalPrice && (
            <StyledOldTotalPriceWrapper>
              {!disableSalePrice && (
                <StyledOldPriceWrapper dualPriceLayout={isDualPriceLayout}>
                  <SCheckoutPrice
                    isColumnLayout={true}
                    price={priceHistoryTotal}
                    isHighLighted={false}
                    isOldPrice={true}
                    dualPriceLayout={isDualPriceLayout}
                  />
                  <AlignedInfoMark
                    content={
                      <>
                        <FormattedMessage {...rest.inSaleInfoMarkText} />
                        <StyledTooltipLink href={rest.inSaleInfoMarkLink} onClick={stopPropagationMouseEvent}>
                          <FormattedMessage {...rest.inSaleInfoMarkTextLink} />
                        </StyledTooltipLink>
                      </>
                    }
                    interactive={true}
                    position={TooltipModel.Position.leftCenter}
                    tooltipWidth={PRODUCT_IN_SALE_TOOLTIP_WIDTH}
                  />
                </StyledOldPriceWrapper>
              )}
              <CheckoutPrice isColumnLayout={true} price={priceTotal} isHighLighted={!!showIsInSale} />
            </StyledOldTotalPriceWrapper>
          )}

          {shouldRenderTotalPrice && (
            <StyledOldTotalPriceWrapper>
              <CheckoutPrice isColumnLayout={true} price={priceTotal} isHighLighted={!!showIsInSale} />
            </StyledOldTotalPriceWrapper>
          )}
          {unitPrice && <UnitPrice unitAmount={unitPrice.unitAmount} price={unitPrice.price} unit={unitPrice.unit} />}
        </StyledPricePerUnitContent>
      </StyledPricePerUnitCol>
    </>
  );
};
PricePerUnit.displayName = 'GeneralProductItem.PricePerUnit';
